/**
 * MMX Testimonials — wheel carousel.
 *
 * Every card is rotated around one shared centre far below the section, so the cards off to
 * the sides tilt away from the middle exactly like the artboard. Advancing spins the wheel
 * back by one step, which brings the next card upright in the centre.
 */

const SWIPE_THRESHOLD = 40;

class MmxTestimonials extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-mmx-testi-stage]');
    this.wheel = this.querySelector('[data-mmx-testi-wheel]');
    if (!this.stage || !this.wheel) return;

    this.cards = Array.from(this.wheel.querySelectorAll('[data-mmx-testi-card]'));
    if (this.cards.length === 0) return;

    this.dotsEl = this.querySelector('[data-mmx-testi-dots]');
    this.prevEl = this.querySelector('[data-mmx-testi-prev]');
    this.nextEl = this.querySelector('[data-mmx-testi-next]');

    this.cards.forEach((card, i) => {
      card.style.setProperty('--mmx-testi-i', i);
      // A peeking card is the most obvious way to reach it — clicking one brings it forward.
      card.addEventListener('click', () => {
        if (this.dragged) return;
        this.goTo(i);
      });
    });

    this.#buildDots();
    this.#bindControls();
    this.#bindSwipe();

    const initial = Number(this.dataset.initial);
    this.index = Number.isFinite(initial) ? initial : Math.floor((this.cards.length - 1) / 2);
    this.goTo(this.index);

    this.#measure();
    this.resizeObserver = new ResizeObserver(() => this.#measure());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /* --------------------------------- Navigation --------------------------------- */

  goTo(index) {
    const next = Math.min(Math.max(index, 0), this.cards.length - 1);
    this.index = next;

    this.wheel.style.setProperty('--mmx-testi-active', next);
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === next));
    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === next);
      dot.setAttribute('aria-current', i === next ? 'true' : 'false');
    });

    if (this.prevEl) this.prevEl.disabled = next === 0;
    if (this.nextEl) this.nextEl.disabled = next === this.cards.length - 1;
  }

  step(direction) {
    this.goTo(this.index + direction);
  }

  #buildDots() {
    if (!this.dotsEl) return;

    this.dots = this.cards.map((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mmx-testi__dot';
      dot.setAttribute('aria-label', card.querySelector('.mmx-testi__pill-text')?.textContent.trim() || `Go to ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsEl.appendChild(dot);
      return dot;
    });
  }

  #bindControls() {
    this.prevEl?.addEventListener('click', () => this.step(-1));
    this.nextEl?.addEventListener('click', () => this.step(1));

    // A single card has nothing to navigate to.
    if (this.cards.length > 1) return;
    this.querySelectorAll('.mmx-testi__controls').forEach((el) => {
      el.style.display = 'none';
    });
  }

  #bindSwipe() {
    let startX = null;

    this.stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      startX = event.clientX;
      this.dragged = false;
    });

    this.stage.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      startX = null;
      if (Math.abs(delta) < SWIPE_THRESHOLD) return;
      // The click that follows the drag would otherwise also select whatever card it landed on.
      this.dragged = true;
      this.step(delta < 0 ? 1 : -1);
    });

    this.stage.addEventListener('pointercancel', () => {
      startX = null;
    });
  }

  /**
   * The cards are absolutely positioned, so the stage needs an explicit height. The tallest
   * card wins: the copy length differs per card and the layout must not jump when the wheel
   * turns.
   */
  #measure() {
    const tallest = this.cards.reduce((max, card) => Math.max(max, card.offsetHeight), 0);
    if (tallest > 0) this.stage.style.setProperty('--mmx-testi-stage-height', `${tallest}px`);
  }
}

if (!customElements.get('mmx-testimonials')) {
  customElements.define('mmx-testimonials', MmxTestimonials);
}
